import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { useScrollToSection } from "../../app/useScrollToSection";
import { getParamAsInt } from "../../app/utils";
import { useAppSelector } from "../../app/hooks";
import { isAdminRole, UserRole } from "../../app/userSlice";
import StepProgressBar, { StepStatus } from "../../components/StepProgressBar";
import AlertMessage, { AlertLevel } from "../../components/AlertMessage";
import { SelectDevelopmentView } from "../../components/input/SelectDevelopmentView";
import RtkqResultViewer from "../../components/RtkqResultViewer";
import { TeacherWritingSubTabs } from "../writing/teacher/TeacherWriting";
import {
	StimulusMaterialSaveModel,
	useAddEditStimulusMaterialMutation,
	StimulusMaterialEditModel,
} from "../stimulusMaterial/stimulusMaterialApiSlice";
import {
	CoursePromptReviewGroup,
	useAddCoursePromptGroupMutation,
} from "../peerReviewGroups/coursePromptReviewGroups/coursePromptReviewGroupsApiSlice";
import {
	AdvancedSettingsViewModel,
	PeerReviewSettingsViewModel,
	SavePromptViewModel,
	GetPromptViewModel,
	useLazyAddPromptDefaultsQuery,
	useAddPromptMutation,
	useLazyEditPromptInfoQuery,
	useEditPromptMutation,
	EmailPromptViewModel,
	useSendNotifyEmailMutation,
	useLazyEmailListQuery,
	EmailPromptListViewModel,
	useLazyAddSuperAdminPromptDefaultsQuery,
	useLazyEditSuperAdminPromptInfoQuery,
	useLazyAddDistrictAdminPromptDefaultsQuery,
	useLazyAddSchoolAdminPromptDefaultsQuery,
	useLazyEditDistrictAdminPromptInfoQuery,
	useLazyEditSchoolAdminPromptInfoQuery,
	useAddSuperAdminPromptMutation,
	useEditSuperAdminPromptMutation,
	useAddDistrictAdminPromptMutation,
	useEditDistrictAdminPromptMutation,
	useAddSchoolAdminPromptMutation,
	useEditSchoolAdminPromptMutation,
} from "./promptsApiSlice";
import PromptStep from "./PromptStep";
import AssignmentStep from "./AssignmentStep";
import OrganizerStep from "./OrganizerStep";
import OptionsStep from "./OptionsStep";
import PeerReviewStep from "./PeerReviewStep";
import SharePromptStep from "./SharePromptStep";

export enum PromptSteps {
	Prompt = 1,
	Assignment,
	Organizer,
	PeerReviews,
	Options,
	Share,
}

const initialSteps = [
	{ id: PromptSteps.Prompt, name: "Prompt", status: StepStatus.Current },
	{ id: PromptSteps.Assignment, name: "Assignment", status: StepStatus.Upcoming },
	{ id: PromptSteps.Organizer, name: "Organizer", status: StepStatus.Upcoming },
	{ id: PromptSteps.PeerReviews, name: "Peer Reviews", status: StepStatus.Upcoming },
	{ id: PromptSteps.Options, name: "Options", status: StepStatus.Upcoming },
	{ id: PromptSteps.Share, name: "Share Prompt", status: StepStatus.Upcoming },
];

export default function AddEditPrompt() {
	const [queryParams] = useSearchParams();
	const promptId = getParamAsInt(queryParams, "promptId");
	const isEdit = !!promptId;
	const userRole = useAppSelector((state) => state.user.role);
	const isAdminMode = isAdminRole(userRole);
	useScrollToSection();

	const [view, setView] = useState<string>("react");

	const [step, setStep] = useState(PromptSteps.Prompt);
	const [steps, setSteps] = useState(initialSteps);
	const [disableSave, setDisableSave] = useState(true);
	const [promptStepHasError, setPromptStepHasError] = useState(true);
	const [assignmentStepHasError, setAssignmentStepHasError] = useState(false);
	const [optionsStepHasError, setOptionsStepHasError] = useState(false);
	const [model, setModel] = useState<SavePromptViewModel>();
	const [advancedSettingsModel, setAdvancedSettingsModel] = useState<AdvancedSettingsViewModel>();
	const [peerReviewSettingsModel, setPeerReviewSettingsModel] =
		useState<PeerReviewSettingsViewModel>();
	const [emailListModel, setEmailListModel] = useState<EmailPromptViewModel>();
	const [saveError, setSaveError] = useState("");
	const [newPromptStimulusMaterials, setNewPromptStimulusMaterials] = useState<
		StimulusMaterialSaveModel[]
	>([]);

	const [defaults, setDefaults] = useState<GetPromptViewModel>();

	// Teacher
	const [addPrompt] = useAddPromptMutation();
	const [editPrompt] = useEditPromptMutation();
	const [getNewPromptDefaults, getNewPromptDefaultsResult] = useLazyAddPromptDefaultsQuery();
	const [getEditPromptDefaults, getEditPromptDefaultsResult] = useLazyEditPromptInfoQuery();
	// Super / State
	const [addSuperAdminPrompt] = useAddSuperAdminPromptMutation();
	const [editSuperAdminPrompt] = useEditSuperAdminPromptMutation();
	const [getSuperAdminNewPromptDefaults, getNewSuperAdminPromptDefaultsResult] =
		useLazyAddSuperAdminPromptDefaultsQuery();
	const [getSuperAdminEditPromptDefaults, getEditSuperAdminPromptDefaultsResult] =
		useLazyEditSuperAdminPromptInfoQuery();
	// District
	const [addDistrictAdminPrompt] = useAddDistrictAdminPromptMutation();
	const [editDistrictAdminPrompt] = useEditDistrictAdminPromptMutation();
	const [getDistrictAdminNewPromptDefaults, getNewDistrictAdminPromptDefaultsResult] =
		useLazyAddDistrictAdminPromptDefaultsQuery();
	const [getDistrictAdminEditPromptDefaults, getEditDistrictAdminPromptDefaultsResult] =
		useLazyEditDistrictAdminPromptInfoQuery();
	// School
	const [addSchoolAdminPrompt] = useAddSchoolAdminPromptMutation();
	const [editSchoolAdminPrompt] = useEditSchoolAdminPromptMutation();
	const [getSchoolAdminNewPromptDefaults, getNewSchoolAdminPromptDefaultsResult] =
		useLazyAddSchoolAdminPromptDefaultsQuery();
	const [getSchoolAdminEditPromptDefaults, getEditSchoolAdminPromptDefaultsResult] =
		useLazyEditSchoolAdminPromptInfoQuery();

	const [addEditStimMaterial] = useAddEditStimulusMaterialMutation();
	const [addCoursePromptGroup] = useAddCoursePromptGroupMutation();

	const [getEmailList, getEmailListResult] = useLazyEmailListQuery();
	const [emailTeachers] = useSendNotifyEmailMutation();

	const navigate = useNavigate();
	const location = useLocation();

	useEffect(() => {
		if (location.state != null) {
			if (location.state.step) {
				setStep(location.state.step);
				if (promptId) setPromptStepHasError(false);
			}
		}
		navigate(location, { replace: true });
	}, []);

	const getPromptDefaultsByRole = (role: UserRole, promptId: number | undefined) => {
		switch (role) {
			case UserRole.DistrictAdmin:
				return isEdit
					? getDistrictAdminEditPromptDefaults(promptId!)
					: getDistrictAdminNewPromptDefaults();
			case UserRole.SchoolAdmin:
				return isEdit
					? getSchoolAdminEditPromptDefaults(promptId!)
					: getSchoolAdminNewPromptDefaults();
			case UserRole.Admin:
			case UserRole.StateAdmin:
				return isEdit
					? getSuperAdminEditPromptDefaults(promptId!)
					: getSuperAdminNewPromptDefaults();
			default:
				return isEdit ? getEditPromptDefaults(promptId!) : getNewPromptDefaults();
		}
	};

	useEffect(() => {
		const fetchDefaults = () => {
			getPromptDefaultsByRole(userRole!, promptId)
				.unwrap()
				.then((result) => {
					setDefaults(result);
				});
		};

		fetchDefaults();
	}, []);

	function updateSteps() {
		setSteps(
			steps.map((s) =>
				s.id === step
					? {
							...s,
							status:
								(s.id === PromptSteps.Prompt && promptStepHasError) ||
								(s.id === PromptSteps.Assignment && assignmentStepHasError) ||
								(s.id === PromptSteps.Options && optionsStepHasError)
									? StepStatus.Error
									: s.status == StepStatus.Current ||
										  s.status == StepStatus.Complete
										? StepStatus.Complete
										: StepStatus.Current,
						}
					: {
							...s,
							status:
								(s.id === PromptSteps.Prompt && promptStepHasError) ||
								(s.id === PromptSteps.Assignment && assignmentStepHasError) ||
								(s.id === PromptSteps.Options && optionsStepHasError)
									? StepStatus.Error
									: s.status == StepStatus.Upcoming
										? StepStatus.Upcoming
										: StepStatus.Complete,
						}
			)
		);
	}

	useEffect(() => {
		updateSteps();
		const newModel = updateModel(false);
		setModel(newModel);
	}, [step]);

	useEffect(() => {
		if (isAdminMode) {
			setDisableSave(promptStepHasError);
		} else {
			if (!promptStepHasError && !assignmentStepHasError && !optionsStepHasError) {
				setDisableSave(false);
			} else {
				setDisableSave(true);
			}
		}

		updateSteps();
	}, [promptStepHasError, assignmentStepHasError, optionsStepHasError]);

	useEffect(() => {
		setModel(createSaveModel());
		if (!isAdminMode) {
			setAdvancedSettingsModel(createAdvancedSettingsModel());
			setPeerReviewSettingsModel(defaults?.peerReviewSettings);

			if (defaults && !defaults.wasAlreadyShared) {
				var emailModel: EmailPromptListViewModel = {
					promptIds: [],
					includesDisabledPrompts: false,
					courseId:
						Number(
							defaults.promptEnabledInCoursesList.find((c) => c.isCreatedInCourse)
								?.value
						) ?? null,
				};
				getEmailList(emailModel)
					.unwrap()
					.then((model) => {
						setEmailListModel(model);
					});
			}
		}
	}, [defaults]);

	function updateModel(isSaving: boolean): SavePromptViewModel | undefined {
		if (!model) return undefined;

		if (isAdminMode) {
			model.supportedGradeIds = model.grades.map((g) => g.id);
		}

		if (isSaving && advancedSettingsModel && defaults) {
			const resetAdvancedSettingsModel: AdvancedSettingsViewModel = {
				...advancedSettingsModel,
				timeAllowed: advancedSettingsModel.timerEnabled
					? advancedSettingsModel.timeAllowed
					: defaults.advancedSettings.timeAllowed,
				retriesAllowed: advancedSettingsModel.retriesEnabled
					? advancedSettingsModel.retriesAllowed
					: defaults.advancedSettings.retriesAllowed,
				finalizeDraftNumber: advancedSettingsModel.finalizeEnabled
					? advancedSettingsModel.finalizeDraftNumber
					: defaults.advancedSettings.finalizeDraftNumber,
				dueDate: advancedSettingsModel.dueDateEnabled
					? advancedSettingsModel.dueDate
					: defaults.advancedSettings.dueDate,
			};

			const editModel: SavePromptViewModel = {
				...model,
				advancedSettings: resetAdvancedSettingsModel,
				peerReviewSettings: peerReviewSettingsModel,
			};
			return editModel;
		} else {
			const editModel: SavePromptViewModel = {
				...model,
				advancedSettings: advancedSettingsModel,
				peerReviewSettings: peerReviewSettingsModel,
			};
			return editModel;
		}
	}

	function createSaveModel(): SavePromptViewModel | undefined {
		if (!defaults) return undefined;
		const editModel: SavePromptViewModel = {
			isNew: defaults.isNew,
			canEdit: defaults.canEdit,
			enabled: defaults.enabled,
			statusDate: defaults.statusDate,
			name: defaults.name,
			text: defaults.text,
			genreId: defaults.genreId,
			signLanguageVideoURL: defaults.signLanguageVideoURL,
			contentAreaIds: defaults.contentAreaIds,
			contentAreas: defaults.contentAreas,
			grades: defaults.grades,
			advancedSettings: isAdminMode ? undefined : createAdvancedSettingsModel(),
			peerReviewSettings: isAdminMode ? undefined : defaults.peerReviewSettings,
			selectedPromptEnabledInCoursesList: isAdminMode
				? []
				: defaults.promptEnabledInCoursesList.filter((c) => c.selected).map((c) => c.value),
			isShared: defaults.isShared,
			wasAlreadyShared: defaults.wasAlreadyShared,
			supportedGradeIds: defaults.supportedGradeIds,
			active: defaults.active,
		};
		return editModel;
	}

	function createAdvancedSettingsModel(): AdvancedSettingsViewModel | undefined {
		if (!defaults) return undefined;
		const advancedSettings = defaults.advancedSettings;
		const advancedModel: AdvancedSettingsViewModel = {
			assignedToIds: advancedSettings.assignedToIds,
			dueDateEnabled: advancedSettings.dueDateEnabled,
			dueDate: advancedSettings.dueDate,
			dueDateRequired: advancedSettings.dueDateRequired,
			selectedReminders: advancedSettings.selectedReminders,
			timerEnabled: advancedSettings.timerEnabled,
			timeAllowed: advancedSettings.timeAllowed,
			retriesEnabled: advancedSettings.retriesEnabled,
			retriesAllowed: advancedSettings.retriesAllowed,
			titleRequired: advancedSettings.titleRequired,
			requireSources: advancedSettings.requireSources,
			finalizeEnabled: advancedSettings.finalizeEnabled,
			finalizeDraftNumber: advancedSettings.finalizeDraftNumber,
			scoreDraftNumber: advancedSettings.scoreDraftNumber,
			grammarCheckDraftThreshold: advancedSettings.grammarCheckDraftThreshold,
			specializedVocabularyList: advancedSettings.specializedVocabularyList,
			allowCopyPaste: advancedSettings.allowCopyPaste,
			isOrganizerRequired: advancedSettings.isOrganizerRequired,
			selectedOrganizerId: advancedSettings.selectedOrganizerId,
			canSetPlagiarismSettings: advancedSettings.canSetPlagiarismSettings,
			plagiarismCheckerEnabled: advancedSettings.plagiarismCheckerEnabled,
			plagiarismCheckerAllDrafts: advancedSettings.plagiarismCheckerAllDrafts,
			plagiarismCheckedDrafts: advancedSettings.plagiarismCheckedDrafts,
		};
		return advancedModel;
	}

	function handleNextStep() {
		if (step === PromptSteps.Share) return;
		else {
			setStep(step + 1);
		}
	}

	function handlePreviousStep() {
		if (step === PromptSteps.Prompt) return;
		else {
			setStep(step - 1);
		}
	}

	function updateNewPromptStimMaterials(stimMaterials: StimulusMaterialSaveModel[]) {
		setNewPromptStimulusMaterials(stimMaterials);
	}

	function updateStimMaterialsWithPromptId(newPromptId: number): StimulusMaterialSaveModel[] {
		const updatedStimMaterials = newPromptStimulusMaterials.map((sm) => {
			const updatedEditModel: StimulusMaterialEditModel = {
				...sm.editModel,
				promptId: newPromptId,
			};
			const updatedModel: StimulusMaterialSaveModel = { ...sm, editModel: updatedEditModel };
			return updatedModel;
		});
		return updatedStimMaterials;
	}

	const addPromptByRole = (role: UserRole, modelToSave: SavePromptViewModel) => {
		switch (role) {
			case UserRole.DistrictAdmin:
				return addDistrictAdminPrompt(modelToSave);
			case UserRole.SchoolAdmin:
				return addSchoolAdminPrompt(modelToSave);
			case UserRole.Admin:
			case UserRole.StateAdmin:
				return addSuperAdminPrompt(modelToSave);
			default:
				return addPrompt(modelToSave);
		}
	};

	const editPromptByRole = (
		role: UserRole,
		modelToSave: SavePromptViewModel,
		promptId: number
	) => {
		switch (role) {
			case UserRole.DistrictAdmin:
				return editDistrictAdminPrompt({ editPromptId: promptId, model: modelToSave });
			case UserRole.SchoolAdmin:
				return editSchoolAdminPrompt({ editPromptId: promptId, model: modelToSave });
			case UserRole.Admin:
			case UserRole.StateAdmin:
				return editSuperAdminPrompt({ editPromptId: promptId, model: modelToSave });
			default:
				return editPrompt({ editPromptId: promptId, model: modelToSave });
		}
	};

	function handleSave() {
		setDisableSave(true);
		const temp = updateModel(true);

		if (temp) {
			if (promptId) {
				editPromptByRole(userRole!, temp, promptId)
					.unwrap()
					.then((groupErrors) => {
						let errorMessage = "";
						if (!isAdminMode) {
							notifyTeachers(promptId, temp);

							if (groupErrors && groupErrors.length > 0) {
								errorMessage = `The following groups for your prompt, ${temp.name}, could not be saved: `;
								groupErrors.map((error) => {
									errorMessage += `\n${error}`;
								});
							}
						}

						doNavigation(temp.name, promptId, "", errorMessage);
					})
					.catch((error) => {
						handleSaveError(error, temp);
					});
			} else {
				addPromptByRole(userRole!, temp)
					.unwrap()
					.then(async (newPromptId) => {
						let groupsErrorMsg = "";
						if (!isAdminMode) {
							notifyTeachers(newPromptId, temp);

							const groups =
								temp.peerReviewSettings?.groups.coursePromptGroups.groupsPage ?? [];
							if (groups.length > 0) {
								groupsErrorMsg = await AddCoursePromptGroups(
									newPromptId,
									temp.name,
									groups
								);
							}
						}

						let stimErrorMsg = "";
						if (newPromptStimulusMaterials.length > 0) {
							stimErrorMsg = await AddStimulusMaterial(newPromptId, temp.name);
						}

						doNavigation(temp.name, newPromptId, stimErrorMsg, groupsErrorMsg);
					})
					.catch((error) => {
						handleSaveError(error, temp);
					});
			}
		}
	}

	async function AddStimulusMaterial(newPromptId: number, promptName: string): Promise<string> {
		let hasError = false;
		let errorMessage = `The following stimulus material for your prompt, ${promptName}, could not be saved: `;
		const stimulusMaterials = updateStimMaterialsWithPromptId(newPromptId);

		const promises = stimulusMaterials.map(async (sm) => {
			await addEditStimMaterial(sm)
				.unwrap()
				.catch((error) => {
					hasError = true;
					errorMessage += `\n- ${sm.editModel.label}: ${error.data}`;
				});
		});
		await Promise.all(promises);

		if (hasError) {
			return errorMessage;
		}
		return "";
	}

	async function AddCoursePromptGroups(
		newPromptId: number,
		promptName: string,
		groups: CoursePromptReviewGroup[]
	): Promise<string> {
		let hasError = false;
		let errorMessage = `The following groups for your prompt, ${promptName}, could not be saved: `;

		const promises = groups.map(async (group) => {
			await addCoursePromptGroup({ group, promptId: newPromptId })
				.unwrap()
				.catch((errors) => {
					hasError = true;
					errors.data.map((error: string) => {
						errorMessage += `\n${error}`;
					});
				});
		});
		await Promise.all(promises);

		if (hasError) {
			return errorMessage;
		}
		return "";
	}

	function notifyTeachers(promptId: number, promptModel: SavePromptViewModel) {
		if (
			emailListModel &&
			emailListModel.proctorIds?.length > 0 &&
			promptModel.isShared &&
			!promptModel.wasAlreadyShared
		) {
			const model: EmailPromptViewModel = {
				...emailListModel,
				promptIds: [promptId],
				includesDisabledPrompts: !promptModel.enabled,
			};
			emailTeachers(model)
				.unwrap()
				.catch((error) => {
					console.log(error);
				});
		}
	}

	function handleSaveError(error: any, temp: SavePromptViewModel) {
		setSaveError(error.data);
		setStep(PromptSteps.Prompt);
		setModel(temp);
		setAdvancedSettingsModel(temp.advancedSettings);
		setPeerReviewSettingsModel(temp.peerReviewSettings);
		scrollTo(0, 0);
		setDisableSave(false);
	}

	function doNavigation(
		promptName: string,
		promptId: number,
		stimErrorMsg: string,
		groupsErrorMsg: string
	) {
		if (stimErrorMsg || groupsErrorMsg) {
			var error = stimErrorMsg + "\n\n" + groupsErrorMsg;
			navigate(`/${isAdminMode ? "prompts" : "writing"}`, {
				state: {
					errorMessage: error.trim(),
					promptName: promptName,
					promptId: promptId,
					subTab: isAdminMode ? null : TeacherWritingSubTabs.PromptLibrary,
				},
			});
		} else {
			determineNavigationDestination();
		}
	}

	function determineNavigationDestination() {
		isAdminMode
			? navigate("/prompts")
			: navigate("/writing", {
					state: {
						subTab: TeacherWritingSubTabs.PromptLibrary,
					},
				});
	}

	function getResultViewerData() {
		switch (userRole) {
			case UserRole.DistrictAdmin:
				return isEdit
					? getEditDistrictAdminPromptDefaultsResult
					: getNewDistrictAdminPromptDefaultsResult;
			case UserRole.SchoolAdmin:
				return isEdit
					? getEditSchoolAdminPromptDefaultsResult
					: getNewSchoolAdminPromptDefaultsResult;
			case UserRole.Admin:
			case UserRole.StateAdmin:
				return isEdit
					? getEditSuperAdminPromptDefaultsResult
					: getNewSuperAdminPromptDefaultsResult;
			default:
				return isEdit ? getEditPromptDefaultsResult : getNewPromptDefaultsResult;
		}
	}

	return (
		<>
			<section>
				<div className="flex justify-between">
					<div className="w-3/4">
						<h1>{promptId ? "Edit a Prompt" : "Create a Prompt"}</h1>
					</div>
					<SelectDevelopmentView
						value={view}
						setValue={(v) => setView(v as string)}
						simpleListBoxId="viewFilter"
						simpleListBoxClasses="print:hidden"
					/>
				</div>
				<p className="mt-4">
					{isAdminMode ? (
						<>
							Fill in the required fields and click <b>Save Prompt</b> to save the
							prompt. You can only save the prompt once you complete the required
							information.
						</>
					) : (
						<>
							Fill in the required fields and click <b>Save Prompt</b> to save the
							prompt. To navigate through all the steps and options available for
							prompts, click <b>Next Step</b> or <b>Previous Step</b>. You can also
							click on the steps in the progress bar to jump to any step. You can only
							save the prompt once you complete the information in Step 1.
						</>
					)}
				</p>
			</section>
			{view === "react" && (
				<section>
					{!isAdminMode && (
						<StepProgressBar
							activeStep={steps.find((s) => s.id === step)}
							steps={steps}
							setStep={setStep}
							useLargeMediaBreakpoint={true}
						/>
					)}
					{saveError && (
						<AlertMessage
							className="mt-4 text-sm"
							type={AlertLevel.Error}
							message={saveError}
							showIcon={true}
						/>
					)}
					{isAdminMode && defaults && model && (
						<div className="mt-6">
							<PromptStep
								model={model}
								promptId={promptId}
								genres={defaults.essayCategories}
								defaultGrades={defaults.grades}
								availableContentAreas={defaults.availableContentAreas}
								setModel={setModel}
								setHasError={setPromptStepHasError}
								updateNewPromptStimMaterialsModel={updateNewPromptStimMaterials}
							/>
						</div>
					)}
					{defaults && model && advancedSettingsModel && peerReviewSettingsModel && (
						<div className="mt-6">
							{step === PromptSteps.Prompt && (
								<PromptStep
									model={model}
									promptId={promptId}
									genres={defaults.essayCategories}
									availableContentAreas={defaults.availableContentAreas}
									setModel={setModel}
									setHasError={setPromptStepHasError}
									updateNewPromptStimMaterialsModel={updateNewPromptStimMaterials}
								/>
							)}
							{step === PromptSteps.Assignment && (
								<AssignmentStep
									model={advancedSettingsModel}
									availableUsers={defaults.advancedSettings.availableUsers}
									availableReminders={
										defaults.advancedSettings.availableReminders
									}
									setAdvancedModel={setAdvancedSettingsModel}
									setHasError={setAssignmentStepHasError}
								/>
							)}
							{step === PromptSteps.Organizer && (
								<OrganizerStep
									model={advancedSettingsModel}
									organizersByGenre={defaults.advancedSettings.organizersByGenre}
									genres={defaults.essayCategories}
									genreId={model.genreId}
									setAdvancedModel={setAdvancedSettingsModel}
								/>
							)}
							{step === PromptSteps.PeerReviews && (
								<PeerReviewStep
									promptName={model.name}
									model={peerReviewSettingsModel}
									genreId={model.genreId}
									setPeerReviewModel={setPeerReviewSettingsModel}
								/>
							)}
							{step === PromptSteps.Options && (
								<OptionsStep
									model={advancedSettingsModel}
									setAdvancedModel={setAdvancedSettingsModel}
									setHasError={setOptionsStepHasError}
								/>
							)}
							{step === PromptSteps.Share && (
								<SharePromptStep
									model={model}
									emailListModel={emailListModel}
									promptEnabledInCoursesList={defaults.promptEnabledInCoursesList}
									setModel={setModel}
									setEmailListModel={setEmailListModel}
								/>
							)}
						</div>
					)}
					<div className="flex justify-between mt-3">
						<div>
							<button
								id="cancel-button"
								className="btn btn-neutral mr-2"
								onClick={() => determineNavigationDestination()}>
								Cancel
							</button>
							{step != PromptSteps.Prompt && !isAdminMode && (
								<button
									id="previous-step-button"
									className="btn btn-neutral mr-2"
									onClick={handlePreviousStep}>
									Previous Step
								</button>
							)}
						</div>
						<div>
							{step != PromptSteps.Share && !isAdminMode && (
								<button
									id="next-step-button"
									className="btn btn-neutral mr-2"
									onClick={handleNextStep}>
									Next Step
								</button>
							)}
							<button
								id="save-prompt-button"
								className="btn btn-alert mr-2"
								onClick={handleSave}
								disabled={disableSave}>
								Save Prompt
							</button>
						</div>
					</div>
				</section>
			)}

			{view == "raw" && (
				<>
					<RtkqResultViewer
						heading="Default Prompt Info"
						result={getResultViewerData()}
					/>
					<RtkqResultViewer
						heading="Available Teachers List"
						result={getEmailListResult}
					/>
				</>
			)}
		</>
	);
}
